import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Icon,
  Divider,
  useColorModeValue,
  Heading,
  Spinner,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import {
  FaCog,
  FaSave,
  FaArrowLeft,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { customColors } from "../../../atoms/customColors";
import BoletoPreview from "./components/BoletoPreview";
import BolePixApi from "../../../services/bolePixApi";
import jwt_decode from "jwt-decode";
import { useCookies } from "react-cookie";
import { JWTToken } from "../../../routes/privateRoute";
import { goToVendaBolePixListagem } from "../../../routes/coordinator";
import moment from "moment";

// Updated interface to match the provided JSON structure
interface Cliente {
  id: number;
  nome: string;
  sobrenome: string;
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  email: string;
  idPlano: number;
  idTarifa: number;
  idMarkup: number;
  idProjeto: number;
  dataNascProprietario: string;
  apiout: boolean;
}

interface IlistTariffs {
  id: number;
  nome: string;
  urlLogo: string | null;
  caminhoLogo: string | null;
  cor1: string | null;
  cor2: string | null;
  msgE: string | null;
  msgS: string | null;
  msgW: string | null;
  criadoEm: string;
  atualizadoEm: string;
  cliente: Cliente | null;
}

interface BoletoSettingsState {
  cliente: Cliente | null;
  nome: string;
  logo: File | null;
  urlLogo: string | null;
  emailMessage: string;
  smsMessage: string;
  whatsAppMessage: string;
  notificationEnabled: boolean;
  cor1: string;
  cor2: string;
}

/**
 * Especificacao do logotipo do Bolepix.
 *
 * No boleto o logo e renderizado com altura fixa de 45px e largura
 * proporcional (limitada a 300px) - ver components/index.tsx e, no admin,
 * pages/cadastros/boletos/components/Boleto.tsx.
 *
 * A resolucao recomendada e 4x a de exibicao, para o logo ficar nitido em
 * telas retina e na impressao do boleto (300 DPI).
 */
const LOGO_SPEC = {
  IDEAL_WIDTH: 800,
  IDEAL_HEIGHT: 200,
  MIN_HEIGHT: 90,
  MIN_RATIO: 3,
  MAX_RATIO: 5,
  MAX_FILE_SIZE: 1024 * 1024, // 1 MB
  RENDER_HEIGHT: 45,
  RENDER_MAX_WIDTH: 300,
};

interface LogoInfo {
  width: number;
  height: number;
  ratio: number;
  ok: boolean;
  aviso: string | null;
}

const formatarTamanho = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;

const lerDimensoesImagem = (file: File): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Nao foi possivel ler a imagem"));
    };
    img.src = url;
  });

const validarLogo = (width: number, height: number): LogoInfo => {
  const ratio = width / height;
  let aviso: string | null = null;

  if (ratio < LOGO_SPEC.MIN_RATIO) {
    aviso = `Logo muito "em pe" (${ratio.toFixed(1)}:1). Use um logo deitado, entre ${LOGO_SPEC.MIN_RATIO}:1 e ${LOGO_SPEC.MAX_RATIO}:1.`;
  } else if (ratio > LOGO_SPEC.MAX_RATIO) {
    aviso = `Logo muito alongado (${ratio.toFixed(1)}:1). Acima de ${LOGO_SPEC.MAX_RATIO}:1 ele sera reduzido para caber no boleto.`;
  } else if (height < LOGO_SPEC.MIN_HEIGHT) {
    aviso = `Altura de ${height}px e baixa. Use pelo menos ${LOGO_SPEC.MIN_HEIGHT}px para o logo nao sair borrado na impressao.`;
  }

  return { width, height, ratio, ok: aviso === null, aviso };
};

interface BoletoPreviewProps {
  settings: {
    logo?: string;
    colorHeader?: string;
    emailMessage?: string;
    smsMessage?: string;
    whatsAppMessage?: string;
    notificationEnabled?: boolean;
  };
}

export default function EditBoletoSettings() {
  const { id } = useParams<{ id: string }>();
  const [cookies] = useCookies(["auth"]);
  const [settings, setSettings] = useState<BoletoSettingsState>({
    nome: "monedd",
    logo: null,
    cliente: null,
    urlLogo: null,
    emailMessage: "",
    smsMessage: "",
    whatsAppMessage: "",
    notificationEnabled: true,
    cor1: "#20296D",
    cor2: "#20296D",
  });
  const [configId, setConfigId] = useState<number | null>(null);
  const [logoInfo, setLogoInfo] = useState<LogoInfo | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [colors] = useRecoilState(customColors);
  const navigate = useNavigate();
  const cardBg = useColorModeValue(colors.WHITE, "gray.800");
  const [cpf, setCpf] = useState("");

  useEffect(() => {
    const fetchBolePixConfig = async () => {
      if (!id) {
        toast.error("ID do cliente não fornecido!");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const auth = cookies.auth || localStorage.getItem("token");
        let clientName = "";
        if (auth && typeof auth === "string" && auth.includes(".")) {
          const decodedToken: JWTToken = jwt_decode(auth);
          clientName = decodedToken.name || "";
        }

        // Fetch the configuration list
        const dataList: IlistTariffs[] =
          await BolePixApi.getListBolepixConfig();

        // Find configuration where cliente.id matches the provided id
        const config = dataList.find((item) => item.cliente?.id === Number(id));

        if (config && config.cliente) {
          setIsEditMode(true);
          setConfigId(config.id);
          setSettings({
            nome: "monedd", // config.nome || clientName,
            logo: null,
            cliente: config.cliente,
            urlLogo: config.urlLogo,
            emailMessage: config.msgE || "",
            smsMessage: config.msgS || "",
            whatsAppMessage: config.msgW || "",
            notificationEnabled: true,
            cor1: config.cor1 || "#20296D",
            cor2: config.cor2 || "#20296D",
          });
          toast.info("Configuração encontrada para o cliente!");
        } else {
          setIsEditMode(false);
          setConfigId(null);
          setSettings((prev) => ({ ...prev, nome: clientName }));
          toast.info(
            "Nenhuma configuração encontrada. Você pode criar uma nova."
          );
        }
      } catch (error: any) {
        toast.error("Erro ao carregar configurações do BolePix!");
        console.error("Fetch error:", error.response?.data || error.message);
        setIsEditMode(false);
        setSettings((prev) => ({ ...prev, nome: "" }));
      } finally {
        setLoading(false);
      }
    };

    fetchBolePixConfig();
  }, [id, cookies.auth, navigate]);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];

    /**
     * Rejeita o arquivo: limpa o input e o logo em memoria, para que um arquivo
     * invalido nunca fique "meio selecionado" (input mostrando o nome, mas sem
     * o arquivo no state) e acabe salvando o logo anterior sem o cliente notar.
     */
    const rejeitar = (mensagem: string) => {
      toast.error(mensagem);
      input.value = "";
      setLogoInfo(null);
      setSettings((prev) => ({ ...prev, logo: null }));
    };

    if (!file || !file.type.startsWith("image/")) {
      rejeitar("Por favor, selecione uma imagem válida!");
      return;
    }

    if (file.size > LOGO_SPEC.MAX_FILE_SIZE) {
      rejeitar(
        `Imagem muito pesada: ${formatarTamanho(file.size)}. ` +
        `O limite é ${formatarTamanho(LOGO_SPEC.MAX_FILE_SIZE)} — comprima a imagem e envie novamente.`
      );
      return;
    }

    try {
      const { width, height } = await lerDimensoesImagem(file);
      const info = validarLogo(width, height);
      setLogoInfo(info);

      if (info.aviso) {
        toast.warning(info.aviso);
      }

      setSettings((prev) => ({ ...prev, logo: file, urlLogo: null }));
    } catch (error) {
      rejeitar("Não foi possível ler a imagem selecionada!");
    }
  };

  const handleSave = async () => {
    try {
      if (!settings.logo && !settings.urlLogo) {
        toast.error("Selecione uma logo antes de salvar!");
        return;
      }

      // Rede de seguranca: garante que nenhum arquivo acima do limite seja enviado.
      if (settings.logo && settings.logo.size > LOGO_SPEC.MAX_FILE_SIZE) {
        toast.error(
          `A logo excede o limite de ${formatarTamanho(LOGO_SPEC.MAX_FILE_SIZE)}. Envie uma imagem menor.`
        );
        return;
      }

      if (!settings.nome) {
        toast.error("O nome da configuração é obrigatório!");
        return;
      }

      const hexCor1 = settings.cor1.startsWith("#")
        ? settings.cor1.toUpperCase()
        : `#${settings.cor1.toUpperCase()}`;
      const hexCor2 = settings.cor2.startsWith("#")
        ? settings.cor2.toUpperCase()
        : `#${settings.cor2.toUpperCase()}`;

      const formData = new FormData();
      formData.append("nome", settings.nome);
      if (settings.logo) {
        formData.append("file", settings.logo);
      }
      formData.append("cor1", hexCor1);
      formData.append("cor2", hexCor2);
      formData.append("msgE", settings.emailMessage || "");
      formData.append("msgS", settings.smsMessage || "");
      formData.append("msgW", settings.whatsAppMessage || "");
      if (id) {
        formData.append("idClientes", id.toString());
      }
      if (isEditMode && configId) {
        formData.append("id", configId.toString());
      }

      console.log("FormData entries:", Array.from(formData.entries()));

      await BolePixApi.upsertBolepixConfig(formData);
      toast.success(
        isEditMode
          ? "Configurações atualizadas com sucesso!"
          : "Configuração criada com sucesso!"
      );
      goToVendaBolePixListagem(navigate);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || "Erro desconhecido";
      toast.error(
        `Erro ao ${isEditMode ? "atualizar" : "criar"
        } configurações: ${errorMessage}`
      );
      console.error("Save error:", error.response?.data || error.message);
    }
  };

  if (loading) {
    return (
      <Flex
        direction="column"
        height="85vh"
        width="100%"
        align="center"
        justify="center"
        bgColor="gray.100"
      >
        <Spinner size="xl" color={colors.BLUEMONEDDBUTTONS} />
      </Flex>
    );
  }

  if (!id) {
    return (
      <Flex
        direction="column"
        height="85vh"
        width="100%"
        align="center"
        justify="center"
        bgColor="gray.100"
      >
        <Text color={colors.REDMONEDD} fontSize="lg">
          ID do cliente não fornecido!
        </Text>
        <Button
          mt={4}
          bg={colors.BLUEMONEDDBUTTONS}
          color={colors.WHITE}
          onClick={() => goToVendaBolePixListagem(navigate)}
        >
          Voltar
        </Button>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      height="85vh"
      width="100%"
      overflowY="auto"
      bgColor="gray.100"
      p={10}
      gap={8}
    >
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={3}>
          <Icon as={FaCog} w={7} h={7} color={colors.BLUEMONEDDBUTTONS} />
          <Heading size="lg" color={colors.BLUEMONEDDBUTTONS}>
            {isEditMode
              ? "Editar Configuração de Bolepix"
              : "Criar Configuração de Bolepix"}
          </Heading>
        </Flex>
        <Button
          leftIcon={<FaArrowLeft />}
          bg={colors.GRAY}
          color={colors.WHITE}
          _hover={{ bg: "gray.600" }}
          onClick={() => goToVendaBolePixListagem(navigate)}
        >
          Voltar
        </Button>
      </Flex>

      {!isEditMode && (
        <Text color={colors.BLUEMONEDDBUTTONS} fontSize="md" mb={4}>
          Nenhuma configuração encontrada para este cliente. Preencha os campos
          para criar uma nova configuração.
        </Text>
      )}

      <Flex direction={{ base: "column", lg: "row" }} gap={8}>
        <Box flex="1" bg={cardBg} p={8} borderRadius="2xl" boxShadow="xl">
          {/* <FormControl mb={6}>
            <FormLabel fontWeight="semibold" color={colors.BLUEMONEDDBUTTONS}>
              Nome da configuração
            </FormLabel>
            <Input
              type="text"
              value={settings.nome}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, nome: e.target.value }))
              }
              bg={colors.WHITE}
              borderColor="gray.300"
              _focus={{ borderColor: colors.BLUEMONEDDBUTTONS }}
            />
          </FormControl> */}

          {/* <FormControl>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
              CPF/CNPJ
            </FormLabel>
            <Input
              size="sm"
              value={settings.cliente?.cnpj || ""}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, cnpj: e.target.value }))
              }
              placeholder="999.999.999-99 ou 99.999.999/9999-99"
              bg={colors.WHITE}
              borderColor={colors.GRAY_LIGHTER}
              _focus={{ borderColor: colors.BLUEMONEDDBUTTONS }}
              _hover={{ borderColor: colors.BLUEMONEDDRGB2 }}
            />
          </FormControl> */}

          <FormControl mb={6}>
            <FormLabel fontWeight="semibold" color={colors.BLUEMONEDDBUTTONS}>
              Logotipo do Bolepix
            </FormLabel>

            {/* Especificacao visivel para o cliente saber o que enviar */}
            <Box
              bg="blue.50"
              border="1px solid"
              borderColor="blue.200"
              borderRadius="md"
              p={4}
              mb={3}
            >
              <Flex align="center" gap={2} mb={2}>
                <Icon as={FaInfoCircle} color={colors.BLUEMONEDDBUTTONS} />
                <Text
                  fontWeight="bold"
                  fontSize="sm"
                  color={colors.BLUEMONEDDBUTTONS}
                >
                  Resolução recomendada: {LOGO_SPEC.IDEAL_WIDTH} × {LOGO_SPEC.IDEAL_HEIGHT} px
                  (proporção 4:1 — logo deitado)
                </Text>
              </Flex>
              <Box as="ul" pl={5} fontSize="xs" color="gray.700" lineHeight="1.8">
                <li>
                  <b>Formato:</b> PNG com fundo transparente (evita o logo
                  aparecer com “caixa” branca no boleto)
                </li>
                <li>
                  <b>Proporção aceita:</b> de {LOGO_SPEC.MIN_RATIO}:1 a{" "}
                  {LOGO_SPEC.MAX_RATIO}:1 — sempre deitado, nunca quadrado ou em pé
                </li>
                <li>
                  <b>Altura mínima:</b> {LOGO_SPEC.MIN_HEIGHT} px (abaixo disso o
                  logo sai borrado ao imprimir)
                </li>
                <li>
                  <b>Peso máximo:</b> {formatarTamanho(LOGO_SPEC.MAX_FILE_SIZE)}{" "}
                  (arquivos maiores são recusados)
                </li>
              </Box>
              <Text fontSize="xs" color="gray.600" mt={2} fontStyle="italic">
                No boleto o logo é exibido com {LOGO_SPEC.RENDER_HEIGHT} px de
                altura (e no máximo {LOGO_SPEC.RENDER_MAX_WIDTH} px de largura).
                Enviar em alta resolução mantém a nitidez na impressão.
              </Text>
            </Box>

            <Input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={handleLogoUpload}
              border="1px dashed"
              borderColor="gray.400"
              p={2}
              cursor="pointer"
              bg={colors.WHITE}
            />

            {/* Feedback do que o cliente acabou de enviar */}
            {logoInfo && (
              <Flex
                mt={2}
                align="center"
                gap={2}
                fontSize="sm"
                color={logoInfo.ok ? "green.600" : "orange.600"}
              >
                <Icon as={logoInfo.ok ? FaCheckCircle : FaExclamationTriangle} />
                <Text>
                  {logoInfo.width} × {logoInfo.height} px ({logoInfo.ratio.toFixed(1)}:1)
                  {logoInfo.ok
                    ? " — dentro do padrão"
                    : ` — ${logoInfo.aviso}`}
                </Text>
              </Flex>
            )}

            {settings.logo ? (
              <Text mt={2} fontSize="sm" color={colors.BLUEMONEDDBUTTONS}>
                Arquivo selecionado: {settings.logo.name}
              </Text>
            ) : null}
          </FormControl>

          <FormControl mb={6}>
            <FormLabel fontWeight="semibold" color={colors.BLUEMONEDDBUTTONS}>
              Cor do cabeçalho
            </FormLabel>
            <Input
              type="color"
              value={settings.cor1}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, cor1: e.target.value }))
              }
              w="100px"
              h="50px"
              p={0}
              cursor="pointer"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.300"
            />
          </FormControl>

          <Divider my={6} />

          <FormControl mb={6}>
            <FormLabel fontWeight="semibold" color={colors.BLUEMONEDDBUTTONS}>
              Mensagem no corpo do E-mail
            </FormLabel>
            <Textarea
              value={settings.emailMessage}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  emailMessage: e.target.value,
                }))
              }
              placeholder="Digite a mensagem padrão para o e-mail..."
              bg={colors.WHITE}
              borderColor="gray.300"
              minH="100px"
            />
          </FormControl>

          <Flex justify="flex-end">
            <Button
              bg={colors.BLUEMONEDDBUTTONS}
              color={colors.WHITE}
              leftIcon={<FaSave />}
              _hover={{ bg: "blue.700" }}
              px={6}
              onClick={handleSave}
            >
              {isEditMode ? "Salvar Configurações" : "Criar Configuração"}
            </Button>
          </Flex>
        </Box>

        <Box flex="1">
          <BoletoPreview
            settings={{
              logo: settings.logo
                ? URL.createObjectURL(settings.logo)
                : settings.urlLogo || undefined,
              colorHeader: settings.cor1,
              emailMessage: settings.emailMessage,
              smsMessage: settings.smsMessage,
              whatsAppMessage: settings.whatsAppMessage,
              notificationEnabled: settings.notificationEnabled,
            }}
          />
        </Box>
      </Flex>
    </Flex>
  );
}
